// Matching free-typed dancer names to the names stored in the app's results.
//
// Names reach us in many spellings: "Μαρία Παπαδοπούλου", "MARIA PAPADOPOULOU",
// "Papadopoulou Maria", "Alexandra Mpountouri" (Greek μπ → mp) ...
//   • EXACT  – same name once case, accents, Greek/Latin spelling and word order are ignored,
//              and only ONE database name matches → linked automatically.
//   • REVIEW – similar names exist (typos, Papadopoulos/Papadopoulou, several candidates)
//              → an admin must confirm which one is the same person.
//   • NONE   – nothing similar → not in the database.

const GREEK_DIGRAPHS = [
  ['ου', 'u'], ['αι', 'e'], ['ει', 'i'], ['οι', 'i'], ['υι', 'i'],
  ['γγ', 'g'], ['γκ', 'g']
];
const GREEK_LETTERS = {
  α: 'a', β: 'v', γ: 'g', δ: 'd', ε: 'e', ζ: 'z', η: 'i', θ: 't', ι: 'i', κ: 'k', λ: 'l',
  μ: 'm', ν: 'n', ξ: 'x', ο: 'o', π: 'p', ρ: 'r', σ: 's', ς: 's', τ: 't', υ: 'i', φ: 'f',
  χ: 'x', ψ: 'ps', ω: 'o'
};

const stripAccents = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');

/** One word → a phonetic key shared by the Greek and the common Latin spellings. */
export const phoneticToken = (word) => {
  let w = stripAccents(String(word || '').toLowerCase());

  // Greek letters → Latin sound
  if (/[Ͱ-Ͽ]/.test(w)) {
    if (w.startsWith('μπ')) w = `b${w.slice(2)}`;
    if (w.startsWith('ντ')) w = `d${w.slice(2)}`;
    // αυ / ευ sound like av / ev, but af / ef before an unvoiced consonant or at the end
    w = w.replace(/([αε])υ(?=[κξπσςτφχψθ]|$)/g, '$1f').replace(/([αε])υ/g, '$1v');
    GREEK_DIGRAPHS.forEach(([g, l]) => { w = w.split(g).join(l); });
    w = [...w].map((ch) => GREEK_LETTERS[ch] || ch).join('');
  }

  w = w.replace(/[^a-z]/g, '');
  // Latin transliteration of Greek: Mpountouri = Boundouri, Ntina = Dina ...
  w = w.replace(/^mp/, 'b').replace(/^nt/, 'd').replace(/^gk/, 'g');
  w = w
    .replace(/ou/g, 'u').replace(/oy/g, 'u').replace(/ei/g, 'i').replace(/oi/g, 'i').replace(/ai/g, 'e')
    .replace(/ph/g, 'f').replace(/th/g, 't').replace(/ch|kh/g, 'x').replace(/ks/g, 'x')
    .replace(/gh/g, 'g').replace(/y/g, 'i').replace(/j/g, 'i').replace(/w/g, 'o')
    .replace(/c/g, 'k').replace(/b/g, 'v')
    .replace(/(.)\1+/g, '$1');
  return w;
};

export const nameTokens = (name) =>
  stripAccents(String(name || ''))
    .split(/[\s\-.,_/]+/)
    .filter(Boolean)
    .map(phoneticToken)
    .filter(Boolean);

/** Order-independent key: "Papadopoulou Maria" and "Μαρία Παπαδοπούλου" share it. */
export const nameKey = (name) => nameTokens(name).sort().join(' ');

const levenshtein = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[b.length];
};

const tokenSimilarity = (a, b) => {
  if (a === b) return 1;
  // initials: "m" ~ "maria"
  if (a.length === 1 || b.length === 1) return a[0] === b[0] ? 0.6 : 0;
  return 1 - levenshtein(a, b) / Math.max(a.length, b.length);
};

/** Greedy best pairing of tokens, averaged over the longer name. 1 = identical. */
export const nameSimilarity = (tokensA, tokensB) => {
  const [short, long] = tokensA.length <= tokensB.length ? [tokensA, tokensB] : [tokensB, tokensA];
  const used = new Set();
  let total = 0;
  short.forEach((t) => {
    let best = 0;
    let bestIdx = -1;
    long.forEach((u, idx) => {
      if (used.has(idx)) return;
      const s = tokenSimilarity(t, u);
      if (s > best) { best = s; bestIdx = idx; }
    });
    if (bestIdx >= 0) used.add(bestIdx);
    total += best;
  });
  return total / long.length;
};

/** Build once from every dancer name in the app, then call matchDancer many times. */
export const buildNameIndex = (names) => {
  const byKey = new Map();
  const entries = [];
  const seen = new Set();
  (names || []).forEach((name) => {
    if (!name || seen.has(name)) return;
    seen.add(name);
    const tokens = nameTokens(name);
    if (!tokens.length) return;
    const key = [...tokens].sort().join(' ');
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(name);
    entries.push({ name, tokens, prefixes: new Set(tokens.map((t) => t.slice(0, 2))) });
  });
  return { byKey, entries };
};

const REVIEW_THRESHOLD = 0.8;
const MAX_CANDIDATES = 3;

/**
 * @returns {{status: 'exact'|'review'|'none', match: string|null, candidates: {name:string, score:number}[]}}
 */
export const matchDancer = (input, index, { fuzzy = true } = {}) => {
  const tokens = nameTokens(input);
  if (!tokens.length) return { status: 'none', match: null, candidates: [] };

  const sameKey = index.byKey.get([...tokens].sort().join(' ')) || [];
  if (sameKey.length === 1) return { status: 'exact', match: sameKey[0], twins: sameKey, candidates: [] };

  // Several database names have the same name once spelling is ignored (typically one dancer stored
  // twice: Greek letters + Latin letters, or different capitalisation / word order). They are the same
  // person for results and rankings, so we link them – the caller picks which spelling to show.
  if (sameKey.length > 1 && tokens.length > 1) {
    return { status: 'exact', match: sameKey[0], twins: sameKey, candidates: [] };
  }

  const candidates = new Map(sameKey.map((n) => [n, 1]));
  if (tokens.length === 1) {
    // a single word is too ambiguous to guess from similarity alone
    return {
      status: candidates.size ? 'review' : 'none',
      match: null,
      candidates: [...candidates].map(([name, score]) => ({ name, score })).slice(0, MAX_CANDIDATES)
    };
  }

  if (!fuzzy) return { status: candidates.size ? 'review' : 'none', match: null, candidates: [] };
  const wanted = new Set(tokens.map((t) => t.slice(0, 2)));
  index.entries.forEach(({ name, tokens: t, prefixes }) => {
    if (candidates.has(name)) return;
    let share = false;
    prefixes.forEach((p) => { if (wanted.has(p)) share = true; });
    if (!share) return;
    const score = nameSimilarity(tokens, t);
    if (score >= REVIEW_THRESHOLD) candidates.set(name, score);
  });

  const list = [...candidates]
    .map(([name, score]) => ({ name, score: Math.round(score * 100) / 100 }))
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_CANDIDATES);
  return { status: list.length ? 'review' : 'none', match: null, candidates: list };
};

/* ───────────────────────── pasted start lists ───────────────────────── */
// Real start lists are rarely clean: "12. Papadopoulou Maria<TAB>Prima Dance", "Maria Papadopoulou - Kinisi",
// "ΜΑΡΙΑ ΠΑΠΑΔΟΠΟΥΛΟΥ (Prima)", numbered lines, name + studio in one cell, "Surname, Name" ...

const NUMBERING = /^\s*(?:#\s*)?\d{1,4}\s*[.)\]:\-–]?\s+/;
const COLUMN_SEPARATOR = /\t+|\s{2,}|\s[|;–—-]\s|[|;]/;
const squash = (s) => s.replace(/[(),]/g, ' ').replace(/\s+/g, ' ').trim();

/** Every plausible "this is the dancer's name" string in one pasted line (whole line first). */
export const lineCandidates = (rawLine) => {
  const line = String(rawLine || '').replace(/ /g, ' ').trim().replace(NUMBERING, '').trim();
  if (!line) return [];
  const columns = line.split(COLUMN_SEPARATOR).map((c) => c.trim()).filter((c) => c && !/^[\d\s.,#-]+$/.test(c));
  const flat = squash(line.replace(/[|;]/g, ' '));
  const out = new Set([flat]);
  columns.forEach((c) => out.add(squash(c)));
  for (let i = 0; i + 1 < columns.length; i++) out.add(squash(`${columns[i]} ${columns[i + 1]}`));
  // a studio / club typed after the name in the same cell: try the name-sized word windows
  const words = flat.split(' ');
  if (words.length > 2 && words.length <= 10) {
    for (let len = Math.min(words.length - 1, 4); len >= 2; len--) {
      for (let start = 0; start + len <= words.length; start++) out.add(words.slice(start, start + len).join(' '));
    }
  }
  return [...out].filter(Boolean);
};

/**
 * Best database match for one pasted line.
 * @returns {null | {typed: string, status: 'exact'|'review'|'none', match: string|null, twins?: string[], candidates: object[]}}
 */
export const matchPastedLine = (rawLine, index) => {
  const cands = lineCandidates(rawLine);
  if (!cands.length) return null;
  const results = cands.map((c, order) => ({ c, order, tokens: nameTokens(c).length, m: matchDancer(c, index, { fuzzy: false }) }));
  const named = results.filter((r) => r.tokens >= 2); // a single word (e.g. a studio) is never a safe match
  const pool = named.length ? named : results;

  // 1) recognised exactly – prefer the longest recognised name, then the earliest candidate
  const exact = pool.filter((r) => r.m.status === 'exact').sort((a, b) => b.tokens - a.tokens || a.order - b.order)[0];
  if (exact) return { typed: exact.c, ...exact.m };

  // 2) similar names – fuzzy search on the first few name-sized candidates only, strongest similarity wins
  pool.filter((r) => r.tokens <= 4).slice(0, 6).forEach((r) => { r.m = matchDancer(r.c, index); });
  const review = pool.filter((r) => r.m.status === 'review')
    .sort((a, b) => b.m.candidates[0].score - a.m.candidates[0].score || b.tokens - a.tokens || a.order - b.order)[0];
  if (review) return { typed: review.c, ...review.m };

  // 3) not in the database: show the cleanest name-sized guess instead of the raw line
  const flat = cands[0];
  const nameSized = cands.find((c) => { const n = c.split(' ').length; return n >= 2 && n <= 4; });
  return { typed: flat.split(' ').length <= 4 ? flat : (nameSized || flat), status: 'none', match: null, candidates: [] };
};
